import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import {
  SakuraFlower,
  CherryBranchImg,
} from "../components/SakuraDecorations";

export default function PriserPage() {
  const priceList = [
    {
      title: "1. Behandling (voksne)",
      desc: (
        <>
          Grundig samtale,{" "}
          <Link to="/japansk-akupunktur" className="text-[#d74814] underline hover:text-[#593427]">
            puls- og mavediagnose
          </Link>{" "}
          samt første nænsomme nålebehandling.
        </>
      ),
      price: "600 kr.",
      badge: "Første Konsultation",
      highlight: true,
      cherryIndex: 1,
    },
    {
      title: "Efterfølgende behandlinger (voksne)",
      desc: "Opfølgende nålebehandling, tilpasset løbende respons og kroppens signaler.",
      price: "500 kr.",
      badge: "Opfølgning",
      cherryIndex: 2,
    },
    {
      title: "1. Behandling (børn under 12 år)",
      desc: (
        <>
          Ekstra nænsom tilgang med ekstremt tynde nåle eller kontaktnåle (
          <Link to="/japansk-akupunktur" className="text-[#d74814] underline hover:text-[#593427]">
            japansk akupunktur
          </Link>
          ) tilpasset barnet.
        </>
      ),
      price: "500 kr.",
      badge: "Børn < 12 år",
      cherryIndex: 3,
    },
    {
      title: "Efterfølgende behandlinger (børn)",
      desc: "Korte, blide opfølgningsbehandlinger for børn under 12 år.",
      price: "400 kr.",
      badge: "Børn Opfølgning",
      cherryIndex: 4,
    },
    {
      title: "Rygestop (1. behandling)",
      desc: (
        <>
          Målrettet{" "}
          <Link to="/rygestop-behandling" className="text-[#d74814] underline hover:text-[#593427]">
            rygestop-forløb
          </Link>{" "}
          med kropsakupunktur og sterile{" "}
          <Link to="/oere-akupunktur" className="text-[#d74814] underline hover:text-[#593427]">
            ørenåle/permanåle
          </Link>
          .
        </>
      ),
      price: "600 kr.",
      badge: "Rygestop",
      cherryIndex: 5,
    },
    {
      title: "Rygestop (efterfølgende)",
      desc: (
        <>
          Opfølgende støtte til at fastholde{" "}
          <Link to="/rygestop-behandling" className="text-[#d74814] underline hover:text-[#593427]">
            rygestoppet
          </Link>{" "}
          og dæmpe abstinenser.
        </>
      ),
      price: "500 kr.",
      badge: "Rygestop",
      cherryIndex: 6,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Priser på Akupunktur | Kita Akupunktur</title>
        <meta
          name="description"
          content="Se priser på akupunkturbehandlinger i Risskov. Mulighed for tilskud fra Sygeforsikring “danmark” og sundhedsforsikringer. Gennemskuelige priser."
        />
        <meta property="og:title" content="Priser på Akupunktur | Kita Akupunktur" />
        <meta
          property="og:description"
          content="Se priser på akupunkturbehandlinger i Risskov. Mulighed for tilskud fra Sygeforsikring “danmark” og sundhedsforsikringer. Gennemskuelige priser."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/priser/" />
      </Helmet>

      {/* Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        {/* Botanical Cherry Branch Accent from /design-elements/ (cherry-5.png) */}
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={5} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>PRISOVERSIGT • KITA AKUPUNKTUR</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427]">Priser</h1>
          <p className="text-base sm:text-lg text-[#7a5b4f] mt-3 max-w-2xl">
            Gennemskuelige priser og mulighed for tilskud hos RAB-registreret akupunktør hos{" "}
            <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
              Kita Akupunktur
            </Link>{" "}
            i Risskov.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 space-y-12">
        {/* Price Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {priceList.map((item, idx) => (
            <div
              key={idx}
              className={`p-6 sm:p-7 rounded-3xl border transition-all duration-300 flex flex-col justify-between relative overflow-hidden ${
                item.highlight
                  ? "glass-sakura border-[#ffe0cf] shadow-[0_12px_32px_rgba(215,72,20,0.12)]"
                  : "glass-card border-[#ffe0cf] shadow-[0_8px_24px_rgba(89,52,39,0.04)] hover:bg-white"
              }`}
            >
              {/* Subtle decorative branch in card */}
              <div className="absolute -right-4 -bottom-4 w-28 opacity-10 pointer-events-none">
                <CherryBranchImg index={item.cherryIndex} className="w-full h-auto" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  {item.badge && (
                    <span
                      className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${
                        item.highlight
                          ? "bg-[#e1ef9f] text-[#593427] border border-[#e1ef9f]"
                          : "bg-[#ffe0cf]/60 text-[#d74814] border border-[#ffe0cf]"
                      }`}
                    >
                      <SakuraFlower className="w-3 h-3 mr-1 text-[#d74814]" />
                      {item.badge}
                    </span>
                  )}
                </div>

                <h2 className="text-xl sm:text-2xl font-serif font-semibold text-[#593427] mb-2">
                  {item.title}
                </h2>
                <p className="text-sm leading-relaxed mb-6 text-[#7a5b4f]">
                  {item.desc}
                </p>
              </div>

              <div className="pt-4 border-t border-[#ffe0cf] flex items-center justify-between">
                <div className="text-2xl sm:text-3xl font-serif font-bold text-[#d74814]">
                  {item.price}
                </div>
                <span className="text-xs text-[#7a5b4f] font-medium">pr. session</span>
              </div>
            </div>
          ))}
        </div>

        {/* Section: Tilskud & Photo Split Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-4">
          <div className="lg:col-span-6">
            <div className="glass-card rounded-3xl p-8 sm:p-10 border border-[#ffe0cf] shadow-md space-y-4 relative overflow-hidden">
              <div className="absolute -bottom-8 -right-8 w-40 opacity-15 pointer-events-none">
                <CherryBranchImg index={7} className="w-full h-auto" />
              </div>

              <div className="flex items-center space-x-2.5 mb-2 relative z-10">
                <SakuraFlower className="w-5 h-5 text-[#d74814]" />
                <h2 className="text-2xl font-serif text-[#593427]">
                  Tilskud fra Sygeforsikring “danmark” og sundhedsforsikring
                </h2>
              </div>
              <p className="text-[#7a5b4f] leading-relaxed text-base relative z-10">
                Da jeg er{" "}
                <Link to="/om-mig" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#593427]">
                  RAB-registreret akupunktør
                </Link>
                , kan du få tilskud fra Sygeforsikring “danmark”, hvis du er medlem og har en lægelig diagnose.
              </p>
              <p className="text-[#7a5b4f] leading-relaxed text-base relative z-10">
                Mange private sundhedsforsikringer dækker også akupunkturbehandlinger helt eller delvist. Se flere svar i vores{" "}
                <Link to="/faq" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#593427]">
                  FAQ
                </Link>{" "}
                eller{" "}
                <Link to="/kontakt" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#593427]">
                  kontakt mig
                </Link>{" "}
                for at høre nærmere om dine muligheder.
              </p>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="glass-card rounded-3xl overflow-hidden shadow-lg border border-[#ffe0cf] p-3 relative">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/71339d65-a05d-4c02-a540-2260acdfc245/akupunktur-naal-behandling-arm.jpg"
                alt="Nænsom akupunkturbehandling med sterile engangsnåle hos Kita Akupunktur"
                className="w-full h-[280px] sm:h-[320px] rounded-2xl object-cover"
                loading="lazy"
                width="600"
                height="350"
              />
            </div>
          </div>
        </div>

        {/* Section: Betaling og afbud */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="glass-card p-6 sm:p-7 rounded-2xl border border-[#ffe0cf] shadow-sm">
            <h3 className="font-serif font-semibold text-lg text-[#593427] mb-2 flex items-center">
              <SakuraFlower className="w-4 h-4 mr-2 text-[#d74814]" />
              <span>Betaling</span>
            </h3>
            <p className="text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
              Betaling sker efter hver behandling via MobilePay eller kontant. Kvittering udstedes altid.
            </p>
          </div>
          <div className="glass-card p-6 sm:p-7 rounded-2xl border border-[#ffe0cf] shadow-sm">
            <h3 className="font-serif font-semibold text-lg text-[#593427] mb-2 flex items-center">
              <SakuraFlower className="w-4 h-4 mr-2 text-[#d74814]" />
              <span>Afbudspolitik</span>
            </h3>
            <p className="text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
              Afbud skal meddeles senest 24 timer før den aftalte tid, så tiden kan gives videre til en anden.
            </p>
          </div>
        </div>

        {/* Section: Se alle vores behandlinger */}
        <div className="glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] space-y-6">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs uppercase tracking-wider font-semibold text-[#d74814] block">
              Behandlingsoversigt
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Læs mere om de enkelte behandlingsområder
            </h2>
            <p className="text-[#7a5b4f] text-sm leading-relaxed">
              Alle behandlinger udføres med stor nænsomhed og tilpasses præcist til din krop og dine symptomer.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              { title: "Japansk akupunktur", link: "/japansk-akupunktur" },
              { title: "Klassisk akupunktur", link: "/akupunktur" },
              { title: "Øre akupunktur", link: "/oere-akupunktur" },
              { title: "Stress & søvn", link: "/stress" },
              { title: "Migræne & hovedpine", link: "/migraene" },
              { title: "Ondt i kæben", link: "/ondt-i-kaeben" },
              { title: "Spændinger nakke/skulder", link: "/spaendinger" },
              { title: "Ondt i ryggen & lænd", link: "/ondt-i-ryggen" },
              { title: "Led- & muskelsmerter", link: "/led-og-muskelsmerter" },
              { title: "Vægtregulering", link: "/vaegtregulering" },
              { title: "Rygestop behandling", link: "/rygestop-behandling" },
              { title: "Alle ydelser", link: "/ydelse" },
            ].map((b, i) => (
              <Link
                key={i}
                to={b.link}
                className="p-3.5 rounded-xl bg-white/85 hover:bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] transition-all flex items-center justify-between group shadow-2xs"
              >
                <span>{b.title}</span>
                <i className="fa-solid fa-arrow-right text-[10px] text-[#ffe0cf] group-hover:text-[#d74814] group-hover:translate-x-0.5 transition-all" aria-hidden="true" />
              </Link>
            ))}
          </div>

          <div className="pt-4 flex flex-wrap items-center justify-center gap-4 border-t border-[#ffe0cf]/70">
            <Link
              to="/kontakt"
              className="inline-flex items-center px-6 py-2.5 rounded-full bg-[#d74814] text-white text-xs sm:text-sm font-semibold hover:bg-[#593427] transition-colors shadow-xs"
            >
              <i className="fa-solid fa-calendar-check mr-2" aria-hidden="true" />
              Bestil tid eller kontakt klinikken
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center px-6 py-2.5 rounded-full bg-white text-[#593427] border border-[#ffe0cf] text-xs sm:text-sm font-semibold hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs ofte stillede spørgsmål (FAQ)
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
